// Pending group join (spec 32). A signed-out visitor on /groups/join/{code}
// may sign up by email, Apple/Google or Strava. Only the email and sign-in
// paths can carry a return path; the Strava callback always lands on
// /dashboard. So the join page stores the code here, and AppShell sends the
// visitor back to the join page once they are signed in. The join page
// clears it as soon as a signed-in visitor sees it.

const KEY = "atlas-pending-group-join";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface PendingGroupJoin {
  code: string;
  ref: string | null;
  savedAt: number;
}

export function storePendingGroupJoin(code: string, ref: string | null): void {
  try {
    const value: PendingGroupJoin = { code, ref, savedAt: Date.now() };
    window.localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: the sign-in `next` path still works.
  }
}

export function readPendingGroupJoin(): PendingGroupJoin | null {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingGroupJoin>;
    if (typeof parsed.code !== "string" || !parsed.code || typeof parsed.savedAt !== "number") {
      window.localStorage.removeItem(KEY);
      return null;
    }
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return { code: parsed.code, ref: typeof parsed.ref === "string" ? parsed.ref : null, savedAt: parsed.savedAt };
  } catch {
    return null;
  }
}

export function clearPendingGroupJoin(): void {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * Pull an invite code out of whatever was pasted into "Have a code?": a bare
 * code, a /groups/join/{code} path or a full link (with or without ?ref=).
 */
export function parseGroupCode(input: string): { code: string; ref: string | null } | null {
  const raw = input.trim();
  if (!raw) return null;
  const match = /groups\/join\/([^/?#\s]+)/i.exec(raw);
  if (match?.[1]) {
    const refMatch = /[?&]ref=([^&#\s]+)/i.exec(raw);
    return { code: safeDecode(match[1]), ref: refMatch?.[1] ? safeDecode(refMatch[1]) : null };
  }
  if (/^[A-Za-z0-9-]{4,32}$/.test(raw)) return { code: raw, ref: null };
  return null;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
